import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Play, RotateCcw } from "lucide-react";

const SIZE = 20;
const SPEED = 110;

export function SnakeGame({ onScore }) {
  const [snake, setSnake] = useState([{ x: 8, y: 10 }]);
  const [food, setFood] = useState({ x: 14, y: 10 });
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const [score, setScore] = useState(0);
  const dir = useRef({ x: 1, y: 0 });
  const nextDir = useRef({ x: 1, y: 0 });
  const reported = useRef(false);

  const reset = () => {
    setSnake([{ x: 8, y: 10 }]);
    setFood({ x: 14, y: 10 });
    setScore(0);
    setOver(false);
    dir.current = { x: 1, y: 0 };
    nextDir.current = { x: 1, y: 0 };
    reported.current = false;
    setRunning(true);
  };

  const randFood = (body) => {
    let f;
    do { f = { x: Math.floor(Math.random() * SIZE), y: Math.floor(Math.random() * SIZE) }; }
    while (body.some((s) => s.x === f.x && s.y === f.y));
    return f;
  };

  const tick = useCallback(() => {
    setSnake((prev) => {
      dir.current = nextDir.current;
      const head = { x: prev[0].x + dir.current.x, y: prev[0].y + dir.current.y };
      if (head.x < 0 || head.x >= SIZE || head.y < 0 || head.y >= SIZE ||
          prev.some((s) => s.x === head.x && s.y === head.y)) {
        setRunning(false);
        setOver(true);
        return prev;
      }
      const newSnake = [head, ...prev];
      if (head.x === food.x && head.y === food.y) {
        setScore((s) => s + 10);
        setFood(randFood(newSnake));
      } else {
        newSnake.pop();
      }
      return newSnake;
    });
  }, [food]);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(tick, SPEED);
    return () => clearInterval(id);
  }, [running, tick]);

  useEffect(() => {
    if (over && !reported.current) { reported.current = true; onScore?.(score); }
  }, [over, score, onScore]);

  useEffect(() => {
    const handler = (e) => {
      const d = dir.current;
      const map = {
        ArrowUp: { x: 0, y: -1 }, ArrowDown: { x: 0, y: 1 },
        ArrowLeft: { x: -1, y: 0 }, ArrowRight: { x: 1, y: 0 },
        w: { x: 0, y: -1 }, s: { x: 0, y: 1 }, a: { x: -1, y: 0 }, d: { x: 1, y: 0 },
      };
      const nd = map[e.key];
      if (!nd) return;
      e.preventDefault();
      if (nd.x === -d.x && nd.y === -d.y) return;
      nextDir.current = nd;
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <div className="flex flex-col items-center" data-testid="snake-game">
      <div className="flex items-center justify-between w-full max-w-md mb-4">
        <span className="font-mono text-neon-cyan text-lg" data-testid="snake-score">SCORE: {score}</span>
        {!running && !over && (
          <Button onClick={reset} data-testid="snake-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase">
            <Play className="w-4 h-4 mr-1 fill-black" /> Start
          </Button>
        )}
      </div>

      <div className="relative grid-bg border-2 border-zinc-700 p-1" style={{ width: "min(90vw, 420px)" }}>
        <div className="grid" style={{ gridTemplateColumns: `repeat(${SIZE}, 1fr)`, aspectRatio: "1" }}>
          {Array.from({ length: SIZE * SIZE }).map((_, i) => {
            const x = i % SIZE, y = Math.floor(i / SIZE);
            const isHead = snake[0].x === x && snake[0].y === y;
            const isBody = snake.some((s) => s.x === x && s.y === y);
            const isFood = food.x === x && food.y === y;
            return (
              <div key={i} className="aspect-square"
                style={{
                  background: isHead ? "#00F0FF" : isBody ? "#0aa" : isFood ? "#FF003C" : "transparent",
                  boxShadow: isFood ? "0 0 8px #FF003C" : isHead ? "0 0 8px #00F0FF" : "none",
                }} />
            );
          })}
        </div>

        {over && (
          <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center gap-3" data-testid="snake-gameover">
            <p className="font-display font-black text-3xl text-neon-pink uppercase">Game Over</p>
            <p className="font-mono text-white">Final score: {score}</p>
            <Button onClick={reset} className="bg-neon-cyan text-black rounded-none font-bold uppercase">
              <RotateCcw className="w-4 h-4 mr-1" /> Play Again
            </Button>
          </div>
        )}
        {!running && !over && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <p className="text-zinc-300 font-mono text-sm text-center px-4">Use Arrow Keys / WASD<br />Press Start to play</p>
          </div>
        )}
      </div>
    </div>
  );
}
